import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Minus, Plus, ChevronRight, Truck, Leaf, ShieldCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getProduct, getProducts } from '@/lib/api/endpoints';
import { useCart } from '@/context/CartContext';

const formatPrice = (paiseStr, currency = 'INR') => {
  const rupees = parseInt(paiseStr || '0', 10) / 100;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(rupees);
};

const ProductDetail = () => {
  const { id } = useParams();
  const { addItem } = useCart();
  const [qty, setQty] = useState(1);

  const {
    data: product,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['product', id],
    queryFn: () => getProduct(id),
    enabled: !!id,
  });

  // "Neighbouring signatures" — fetch the next few products
  const { data: relatedRaw = [] } = useQuery({
    queryKey: ['products', 'related', id],
    queryFn: () => getProducts({ per_page: 4, exclude: [Number(id)] }),
    enabled: !!id,
  });
  // Build Your Own Box products (build-box-8/12/24) are Catalog
  // Visibility: Hidden in wp-admin, but the Store API's products list
  // doesn't filter by that — exclude them from "related" the same way
  // Shop.jsx excludes them from the main grid.
  const related = relatedRaw.filter((p) => !/^build-box-\d+$/.test(p.sku || ''));

  if (isLoading) {
    return (
      <main className="container-lux py-32 text-center">
        <Loader2 className="w-6 h-6 animate-spin inline text-[#1a120b]/50" />
      </main>
    );
  }

  if (error || !product) {
    return (
      <div className="container-lux py-32 text-center">
        <p className="font-display text-4xl mb-4">Signature not found</p>
        <Link to="/shop" className="btn-primary">
          Back to the shop
        </Link>
      </div>
    );
  }

  const img =
    product.images?.[0]?.src || product.images?.[0]?.thumbnail;
  const currency = product.prices?.currency_code || 'INR';
  const price = product.prices?.price;
  const mrp = product.prices?.regular_price;
  const onSale =
    mrp && price && mrp !== price && parseInt(mrp, 10) > parseInt(price, 10);

  const handleAdd = async () => {
    try {
      await addItem({ id: product.id, quantity: qty });
      toast.success(`Added ${qty} × ${product.name}`);
    } catch {
      /* toast handled by mutation onError */
    }
  };

  return (
    <main>
      <div className="container-lux py-6">
        <nav className="flex items-center gap-2 text-xs tracking-widest uppercase text-[#1a120b]/60">
          <Link to="/" className="hover:text-[#1a120b]">
            Home
          </Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/shop" className="hover:text-[#1a120b]">
            Shop
          </Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-[#1a120b]">{product.name}</span>
        </nav>
      </div>

      <section className="container-lux grid md:grid-cols-2 gap-16 py-10 md:py-16">
        <div className="relative aspect-square rounded-full bg-[#efe6d3] halo overflow-hidden">
          {img ? (
            <img
              src={img}
              alt={product.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center font-display text-9xl text-[#b78d3c]">
              R
            </div>
          )}
        </div>

        <div className="space-y-6">
          <p className="tracking-luxe text-[#b78d3c]">The House</p>
          <h1 className="font-display text-5xl md:text-6xl leading-tight">
            {product.name}
          </h1>

          {product.short_description && (
            <div
              className="font-serif-alt text-xl text-[#1a120b]/85 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: product.short_description }}
            />
          )}

          <p className="text-sm tracking-widest uppercase text-[#1a120b]/60">
            Premium · Hand-finished
          </p>

          <div className="flex items-baseline gap-3 pt-2">
            <span className="font-display text-3xl">{formatPrice(price, currency)}</span>
            {onSale && (
              <span className="text-[#1a120b]/45 line-through">
                {formatPrice(mrp, currency)}
              </span>
            )}
            {onSale && (
              <span className="text-[#b78d3c] tracking-widest text-xs uppercase">
                On sale
              </span>
            )}
          </div>

          <div className="gold-rule"></div>

          <div className="flex items-center gap-4 pt-2">
            <div className="flex items-center border border-[#1a120b]">
              <button
                onClick={() => setQty(Math.max(1, qty - 1))}
                className="w-11 h-11 flex items-center justify-center hover:bg-[#1a120b]/5"
              >
                <Minus className="w-4 h-4" />
              </button>
              <span className="w-12 text-center">{qty}</span>
              <button
                onClick={() => setQty(qty + 1)}
                className="w-11 h-11 flex items-center justify-center hover:bg-[#1a120b]/5"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={handleAdd}
              className="btn-primary flex-1"
              disabled={!product.is_purchasable}
            >
              Add {formatPrice(String(parseInt(price, 10) * qty), currency)}
            </button>
          </div>

          <button onClick={handleAdd} className="btn-gold w-full">
            Order this signature
          </button>

          <div className="grid grid-cols-3 gap-4 pt-6">
            <div className="flex flex-col items-center text-center gap-2">
              <Truck className="w-5 h-5 text-[#b78d3c]" />
              <p className="text-xs">Free shipping ₹400+</p>
            </div>
            <div className="flex flex-col items-center text-center gap-2">
              <Leaf className="w-5 h-5 text-[#b78d3c]" />
              <p className="text-xs">Small-batch tempered</p>
            </div>
            <div className="flex flex-col items-center text-center gap-2">
              <ShieldCheck className="w-5 h-5 text-[#b78d3c]" />
              <p className="text-xs">100% vegetarian</p>
            </div>
          </div>

          {product.description && (
            <details className="border-t border-[#1a120b]/12 pt-4">
              <summary className="cursor-pointer flex items-center justify-between font-medium">
                Description <Plus className="w-4 h-4" />
              </summary>
              <div
                className="pt-3 text-sm text-[#1a120b]/70 leading-relaxed prose-sm"
                dangerouslySetInnerHTML={{ __html: product.description }}
              />
            </details>
          )}
        </div>
      </section>

      {related.length > 0 && (
        <section className="container-lux pb-24">
          <p className="tracking-luxe text-[#b78d3c] mb-3">
            Continue tasting
          </p>
          <h3 className="font-display text-4xl mb-10">
            Neighbouring signatures
          </h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {related.map((r) => {
              const rimg = r.images?.[0]?.thumbnail || r.images?.[0]?.src;
              return (
                <Link
                  to={`/product/${r.id}`}
                  key={r.id}
                  className="group bg-[#efe6d3] lift block"
                >
                  <div className="aspect-square rounded-full halo overflow-hidden">
                    {rimg ? (
                      <img
                        src={rimg}
                        alt={r.name}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center font-display text-4xl text-[#b78d3c]">
                        R
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <p className="font-display text-lg">{r.name}</p>
                    <p className="text-xs text-[#1a120b]/60 mt-1">
                      {formatPrice(r.prices?.price, r.prices?.currency_code)}
                    </p>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}
    </main>
  );
};

export default ProductDetail;
